import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { X, FileText, Mail, Phone, Building, Calendar, DollarSign, Clock } from 'lucide-react';

interface Quote {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company_type: string | null;
  project_type: string;
  budget: string;
  timeline: string;
  project_description: string;
  status: string | null;
  created_at: string;
}

interface QuotesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuotesModal = ({ isOpen, onClose }: QuotesModalProps) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      fetchQuotes();
    }
  }, [isOpen]);

  const fetchQuotes = async () => {
    try {
      console.log('Fetching quotes...');
      
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error fetching quotes:', error);
        throw error;
      }

      console.log('Quotes fetched successfully:', data);
      setQuotes(data || []);
    } catch (error: any) {
      console.error('Error fetching quotes:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to fetch quotes.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId: string, status: string) => {
    try {
      console.log('Updating quote status:', quoteId, status);
      
      const { error } = await supabase
        .from('quotes')
        .update({ status })
        .eq('id', quoteId);

      if (error) {
        console.error('Supabase error updating quote:', error);
        throw error;
      }

      setQuotes(quotes.map(quote => 
        quote.id === quoteId ? { ...quote, status } : quote
      ));

      toast({
        title: "Success",
        description: `Quote status updated to ${status}.`,
      });
    } catch (error: any) {
      console.error('Error updating quote:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to update quote status.",
      });
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={handleOverlayClick}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl p-6 w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Project Quotes</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            {quotes.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No quotes found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <div
                    key={quote.id}
                    className={`border rounded-lg p-4 transition-colors ${
                      quote.status === 'pending' ? 'bg-yellow-50 border-yellow-200' : 
                      quote.status === 'approved' ? 'bg-green-50 border-green-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold text-gray-900">{quote.name}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            quote.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            quote.status === 'approved' ? 'bg-green-100 text-green-800' :
                            quote.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {quote.status || 'pending'}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-2">
                          <div className="flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {quote.email}
                          </div>
                          {quote.phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 mr-1" />
                              {quote.phone}
                            </div>
                          )}
                          <div className="flex items-center">
                            <DollarSign className="w-4 h-4 mr-1" />
                            {quote.budget}
                          </div>
                          <div className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {quote.timeline}
                          </div>
                        </div>
                        <div className="flex items-center text-xs text-gray-500 mb-2">
                          <Calendar className="w-3 h-3 mr-1" />
                          {new Date(quote.created_at).toLocaleDateString()} at{' '}
                          {new Date(quote.created_at).toLocaleTimeString()}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Project Type:</span> {quote.project_type}
                          {quote.company_type && (
                            <>
                              <span className="mx-2">•</span>
                              <span className="font-medium">Company:</span> {quote.company_type}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <button
                          onClick={() => setSelectedQuote(selectedQuote?.id === quote.id ? null : quote)}
                          className="px-3 py-1 bg-blue-600 text-white text-xs rounded-full hover:bg-blue-700 transition-colors"
                        >
                          {selectedQuote?.id === quote.id ? 'Hide Details' : 'View Details'}
                        </button>
                        {quote.status === 'pending' && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => updateQuoteStatus(quote.id, 'approved')}
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => updateQuoteStatus(quote.id, 'rejected')}
                              className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                            >
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    {selectedQuote?.id === quote.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t pt-3 mt-3"
                      >
                        <h4 className="font-medium text-gray-900 mb-2">Project Description:</h4>
                        <p className="text-gray-700 whitespace-pre-wrap">{quote.project_description}</p>
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default QuotesModal;
